import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, List, ListItem, ListItemText, Stack, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/StarOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorderOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { useAuth } from '../../context/AuthContext';
import { uploadAssetToR2, type AssetUploadKind } from '../../services/r2AssetUpload';
import { addBrandAsset, removeBrandAsset, renameBrandAsset, updateBrandKit, type BrandAsset, type BrandKit } from '../../services/editingApi';
import { debugLog } from '../../utils/debugLog';
import { formatMs } from './editStudioModel';

interface BrandKitDialogProps {
  open: boolean;
  kit: BrandKit | null;
  onClose: () => void;
  onKit: (kit: BrandKit) => void;
  /** Files just uploaded (the studio adds a first logo or track to the edit at once). */
  onUploaded?: (assets: BrandAsset[], kit: BrandKit) => void;
  /** Which section to start the file picker in, when opened from "Upload your logo" or "Upload music". */
  uploadKind?: BrandAsset['kind'] | null;
}

const KIND_LABELS: Record<BrandAsset['kind'], string> = { image: 'Pictures', audio: 'Music and recordings', captions: 'Caption files' };
const ACCEPT: Record<BrandAsset['kind'], string> = { image: 'image/png,image/jpeg,image/webp,image/gif', audio: 'audio/*,.m4a,.mp3,.wav,.aac', captions: '.vtt,.srt,text/vtt' };
const UPLOAD_KIND: Record<BrandAsset['kind'], AssetUploadKind> = { image: 'image', audio: 'audio', captions: 'document' };
const MAX_BYTES: Record<BrandAsset['kind'], number> = { image: 10 * 1024 * 1024, audio: 200 * 1024 * 1024, captions: 2 * 1024 * 1024 };

/** Which section a dropped file belongs in, from its type or name. */
export function kindOfFile(file: { type: string; name: string }): BrandAsset['kind'] | null {
  if (/^image\/(png|jpeg|webp|gif)$/.test(file.type) || /\.(png|jpe?g|webp|gif)$/i.test(file.name)) return 'image';
  if (/^audio\//.test(file.type) || /\.(m4a|mp3|wav|aac|ogg|flac)$/i.test(file.name)) return 'audio';
  if (/\.(vtt|srt)$/i.test(file.name) || file.type === 'text/vtt') return 'captions';
  return null;
}

/** The size of a picture or the length of a sound, read in the browser before upload. */
async function measure(file: File, kind: BrandAsset['kind']): Promise<{ width?: number; height?: number; durationMs?: number }> {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return {};
  const url = URL.createObjectURL(file);
  try {
    if (kind === 'image') {
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve({});
        img.src = url;
      });
    }
    if (kind === 'audio') {
      return await new Promise((resolve) => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve({ durationMs: Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : undefined });
        audio.onerror = () => resolve({});
        audio.src = url;
      });
    }
    return {};
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The account's logo, colour, name, pictures, music, recordings and caption files. */
const BrandKitDialog: React.FC<BrandKitDialogProps> = ({ open, kit, onClose, onKit, onUploaded, uploadKind = null }) => {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [name, setName] = useState(kit?.displayName || '');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [showGenerated, setShowGenerated] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  useEffect(() => { setName(kit?.displayName || ''); }, [kit?.displayName]);
  useEffect(() => {
    // Opened from "Upload your logo" (or music): go straight to the file picker.
    if (open && uploadKind) window.setTimeout(() => inputs.current[uploadKind]?.click(), 150);
  }, [open, uploadKind]);
  useEffect(() => { if (!open) { audioRef.current?.pause(); setPlaying(null); } }, [open]);

  const uploadFiles = async (files: File[], forcedKind?: BrandAsset['kind']) => {
    setProblem(null);
    const uploaded: BrandAsset[] = [];
    let latest: BrandKit | null = null;
    const token = await getToken();
    if (!token) {
      setProblem('Sign in again to upload.');
      return;
    }
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const kind = forcedKind || kindOfFile(file);
      if (!kind) {
        setProblem(`${file.name} isn’t a picture, a sound or a caption file.`);
        continue;
      }
      if (file.size > MAX_BYTES[kind]) {
        setProblem(`${file.name} is too big. ${KIND_LABELS[kind]} go up to ${Math.round(MAX_BYTES[kind] / 1024 / 1024)} MB.`);
        continue;
      }
      setBusy(files.length > 1 ? `Uploading ${i + 1} of ${files.length}: ${file.name}…` : `Uploading ${file.name}…`);
      setProgress(0);
      try {
        const contentType = file.type || (kind === 'captions' ? 'text/vtt' : 'application/octet-stream');
        const stored = await uploadAssetToR2(file, UPLOAD_KIND[kind], token, (percent) => setProgress(percent));
        const measured = await measure(file, kind);
        const before = new Set((latest || kit)?.assets.map((asset) => asset.id) || []);
        latest = await addBrandAsset({ kind, name: file.name.replace(/\.[^.]+$/, '').slice(0, 80) || file.name, objectKey: stored.objectKey, url: stored.url, contentType: stored.contentType || contentType, sizeBytes: stored.sizeBytes || file.size, ...measured });
        onKit(latest);
        const added = latest.assets.find((asset) => !before.has(asset.id));
        if (added) uploaded.push(added);
      } catch (error) {
        debugLog('Brand asset upload failed', error);
        setProblem(error instanceof Error ? error.message : `${file.name} didn’t upload. Try again.`);
      }
    }
    setBusy(null);
    setProgress(null);
    if (latest && uploaded.length && onUploaded) onUploaded(uploaded, latest);
  };

  const remove = async (asset: BrandAsset) => {
    setProblem(null);
    setBusy(`Removing ${asset.name}…`);
    try {
      onKit(await removeBrandAsset(asset.id));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'It couldn’t be removed.');
    } finally {
      setBusy(null);
    }
  };

  const rename = async () => {
    if (!renaming) return;
    const target = renaming;
    setRenaming(null);
    if (!target.name.trim()) return;
    try {
      onKit(await renameBrandAsset(target.id, target.name.trim()));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That didn’t save.');
    }
  };

  const setLogo = async (asset: BrandAsset | null) => {
    setProblem(null);
    try {
      onKit(await updateBrandKit({ logoAssetId: asset ? asset.id : null }));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That didn’t save.');
    }
  };

  const saveName = async () => {
    if ((kit?.displayName || '') === name.trim()) return;
    try {
      onKit(await updateBrandKit({ displayName: name.trim() || null }));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That didn’t save.');
    }
  };

  const saveColour = async (colour: string) => {
    try {
      onKit(await updateBrandKit({ primaryColor: colour.toUpperCase() }));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That didn’t save.');
    }
  };

  const play = (asset: BrandAsset) => {
    if (playing === asset.id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const el = new Audio(asset.url);
    el.onended = () => setPlaying(null);
    el.onerror = () => { setPlaying(null); setProblem('That sound couldn’t be played.'); };
    audioRef.current = el;
    setPlaying(asset.id);
    const started = el.play();
    if (started && typeof started.catch === 'function') started.catch(() => setPlaying(null));
  };

  const row = (asset: BrandAsset, kind: BrandAsset['kind']) => (
    <ListItem
      key={asset.id}
      disableGutters
      secondaryAction={(
        <Stack direction="row">
          {kind === 'audio' ? (
            <IconButton size="small" aria-label={playing === asset.id ? `Stop ${asset.name}` : `Play ${asset.name}`} onClick={() => play(asset)}>{playing === asset.id ? <StopIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}</IconButton>
          ) : null}
          {kind === 'image' ? (
            <Tooltip title={kit?.logoAssetId === asset.id ? 'Your logo' : 'Make this your logo'}>
              <IconButton size="small" aria-label={kit?.logoAssetId === asset.id ? `${asset.name} is the logo` : `Use ${asset.name} as the logo`} onClick={() => setLogo(kit?.logoAssetId === asset.id ? null : asset)}>
                {kit?.logoAssetId === asset.id ? <StarIcon fontSize="inherit" color="primary" /> : <StarBorderIcon fontSize="inherit" />}
              </IconButton>
            </Tooltip>
          ) : null}
          <IconButton size="small" aria-label={`Rename ${asset.name}`} onClick={() => setRenaming({ id: asset.id, name: asset.name })}><EditIcon fontSize="inherit" /></IconButton>
          <IconButton size="small" aria-label={`Remove ${asset.name}`} disabled={Boolean(busy)} onClick={() => remove(asset)}><DeleteOutlineIcon fontSize="inherit" /></IconButton>
        </Stack>
      )}
      sx={{ pr: 16 }}
    >
      {kind === 'image' ? <img src={asset.url} alt="" style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 12, background: '#eee', borderRadius: 4 }} /> : null}
      {renaming?.id === asset.id ? (
        <TextField
          size="small"
          autoFocus
          value={renaming.name}
          onChange={(event) => setRenaming({ id: asset.id, name: event.target.value })}
          onBlur={rename}
          onKeyDown={(event) => { if (event.key === 'Enter') rename(); if (event.key === 'Escape') setRenaming(null); }}
          inputProps={{ maxLength: 80, 'aria-label': 'New name' }}
        />
      ) : (
        <ListItemText
          primary={asset.name}
          secondary={[asset.width && asset.height ? `${asset.width}×${asset.height}` : null, asset.durationMs ? formatMs(asset.durationMs) : null, `${Math.max(1, Math.round(asset.sizeBytes / 1024))} KB`].filter(Boolean).join(' · ')}
          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      )}
    </ListItem>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Brand kit</DialogTitle>
      <DialogContent
        dividers
        onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => { event.preventDefault(); setDragOver(false); const files = Array.from(event.dataTransfer?.files || []); if (files.length) uploadFiles(files); }}
        sx={{ outline: dragOver ? '2px dashed' : 'none', outlineColor: 'primary.main', outlineOffset: -6 }}
        data-testid="brand-kit-drop"
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your logo, colour, pictures, music, recordings and caption files, ready for every quick edit. Only you can see them. Drop files here, or upload several at once.
        </Typography>
        {problem ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProblem(null)}>{problem}</Alert> : null}
        {busy ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption">{busy}</Typography>
            <LinearProgress variant={progress === null ? 'indeterminate' : 'determinate'} value={progress ?? 0} />
          </Box>
        ) : null}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center">
          <TextField size="small" fullWidth label="Your name, as a lower third shows it" value={name} onChange={(event) => setName(event.target.value)} onBlur={saveName} inputProps={{ maxLength: 120, 'data-testid': 'brand-name' }} />
          <TextField size="small" label="Your colour" type="color" value={(kit?.primaryColor || '#3366CC').slice(0, 7)} onChange={(event) => saveColour(event.target.value)} sx={{ width: 110 }} inputProps={{ 'aria-label': 'Brand colour', 'data-testid': 'brand-colour' }} />
        </Stack>
        {(['image', 'audio', 'captions'] as BrandAsset['kind'][]).map((kind) => {
          const all = (kit?.assets || []).filter((asset) => asset.kind === kind);
          const items = all.filter((asset) => !asset.generated);
          const generated = all.filter((asset) => asset.generated);
          return (
            <Box key={kind} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">{KIND_LABELS[kind]}</Typography>
                <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => inputs.current[kind]?.click()} data-testid={`upload-${kind}`}>Upload</Button>
                <input
                  ref={(node) => { inputs.current[kind] = node; }}
                  type="file"
                  accept={ACCEPT[kind]}
                  multiple
                  hidden
                  data-testid={`upload-input-${kind}`}
                  onChange={(event) => { const files = Array.from(event.target.files || []); event.target.value = ''; if (files.length) uploadFiles(files, kind); }}
                />
              </Stack>
              {items.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {kind === 'image' ? 'A PNG with transparency makes the best logo.' : kind === 'audio' ? 'Music you have the rights to, and recorded voiceovers.' : 'VTT or SRT files, if you have captions already.'}
                </Typography>
              ) : (
                <List dense disablePadding>{items.map((asset) => row(asset, kind))}</List>
              )}
              {generated.length ? (
                <>
                  <Button size="small" onClick={() => setShowGenerated((v) => !v)} sx={{ mt: 0.5 }}>
                    {showGenerated ? 'Hide' : 'Show'} {generated.length} generated voice line{generated.length === 1 ? '' : 's'}
                  </Button>
                  <Collapse in={showGenerated} unmountOnExit>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Made by “Generate the voice”. Ones no edit uses are removed after a week.</Typography>
                    <List dense disablePadding>{generated.map((asset) => row(asset, kind))}</List>
                  </Collapse>
                </>
              ) : null}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(busy)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandKitDialog;
